import { useRef } from "react";

import { Link } from "@remix-run/react";
import clsx from "clsx";
import { HeartHandshake, Lock } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "~/components/ui/dialog";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import { Separator } from "~/components/ui/separator";
import { useSessionResearchRelease } from "~/hooks/use-session-research-release";

function ResearchReleasePanel({ sessionId }: { sessionId: string }) {
    const {
        userHasResearchReleaseEnabled,
        sessionHasResearchReleaseEnabled,
        researchReleaseInherited,
        effectiveResearchReleaseEnabled,
        toggleResearchRelease,
        isPending,
        isLoading,
    } = useSessionResearchRelease({ sessionId });

    // AIDEV-NOTE: Track user interaction with the Select via pointer events.
    // Radix Select fires spurious onOpenChange/onValueChange events during initialization.
    // We use a ref to track if a pointer event (click) has occurred on the SelectTrigger,
    // which is a more reliable signal of actual user interaction than onOpenChange. (fixes EVO-761)
    const hasUserClickedTriggerRef = useRef(false);

    const helperText = effectiveResearchReleaseEnabled
        ? "Thank you! The Parts Companion team will use this session to improve the quality of our analysis, without training custom models on your data."
        : "This session will remain private. Nobody will be able to view the session but you.";

    // AIDEV-NOTE: Don't render until data is loaded to prevent Radix Select from
    // firing spurious onValueChange events during hydration/initial render (fixes EVO-761)
    if (isLoading || effectiveResearchReleaseEnabled === undefined) {
        return (
            <div className="flex flex-col space-y-4">
                <div className="text-sm text-brand-forest-800">Loading...</div>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col space-y-4">
                {researchReleaseInherited && userHasResearchReleaseEnabled && (
                    <>
                        <div className="text-sm text-brand-forest-800">
                            This is shared with our team via{" "}
                            <Link
                                to="/settings"
                                className="text-brand-terra-600 underline hover:text-brand-forest-700"
                            >
                                account preferences
                            </Link>
                            . You can keep this session private by selecting
                            "Keep Private" below.
                        </div>
                        <Separator className="bg-brand-forest-100" />
                    </>
                )}

                <div className="space-y-4">
                    <div className="text-sm font-medium text-brand-forest-800">
                        Donate this session to the Parts Companion Project?
                    </div>
                    <Select
                        value={
                            effectiveResearchReleaseEnabled
                                ? "share"
                                : "private"
                        }
                        onValueChange={(value) => {
                            // AIDEV-NOTE: Only process changes if user has clicked the trigger.
                            // Radix Select fires spurious onValueChange events during initialization -
                            // tracking pointer events on trigger is more reliable than onOpenChange. (fixes EVO-761)
                            if (!hasUserClickedTriggerRef.current) {
                                return;
                            }
                            const newValue = value === "share";
                            const currentValue =
                                effectiveResearchReleaseEnabled ??
                                sessionHasResearchReleaseEnabled;
                            if (newValue !== currentValue) {
                                toggleResearchRelease(newValue);
                            }
                        }}
                        disabled={isPending}
                    >
                        <SelectTrigger
                            className="w-full"
                            onPointerDown={() => {
                                hasUserClickedTriggerRef.current = true;
                            }}
                        >
                            <SelectValue placeholder="Select sharing preference" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="private">
                                Keep Private
                            </SelectItem>
                            <SelectItem value="share">
                                Donate to Parts Companion
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="-mx-4 -mb-4 mt-4 border-t border-brand-terra-200 bg-gradient-to-br from-brand-terra-100/40  to-brand-terra-100/60 px-4 py-2 text-sm text-brand-forest-800">
                {helperText}
            </div>
        </>
    );
}

export function ResearchReleaseToggle({ sessionId }: { sessionId: string }) {
    const {
        userHasResearchReleaseEnabled,
        sessionHasResearchReleaseEnabled,
        effectiveResearchReleaseEnabled,
    } = useSessionResearchRelease({ sessionId });
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="default"
                    className="flex items-center gap-2 text-sm"
                >
                    {effectiveResearchReleaseEnabled ? (
                        <>
                            <HeartHandshake className="size-4" />
                            <span>Donated</span>
                        </>
                    ) : (
                        <>
                            <Lock className="mr-1 size-4" />
                            <span>Private</span>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
                <ResearchReleasePanel sessionId={sessionId} />
            </PopoverContent>
        </Popover>
    );
}

export function ResearchReleaseDialog({
    sessionId,
    open,
    onOpenChange,
    showTrigger = true,
}: {
    sessionId: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    showTrigger?: boolean;
}) {
    const {
        userHasResearchReleaseEnabled,
        sessionHasResearchReleaseEnabled,
        effectiveResearchReleaseEnabled,
    } = useSessionResearchRelease({ sessionId });
    return (
        <Dialog
            open={open}
            onOpenChange={onOpenChange}
            analyticsId="research-release-dialog"
        >
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    size="default"
                    className={clsx(
                        "flex items-center gap-2 text-sm px-4",
                        showTrigger && "hidden",
                    )}
                >
                    {effectiveResearchReleaseEnabled ? (
                        <>
                            <HeartHandshake className="size-4" />
                            <span>Donated</span>
                        </>
                    ) : (
                        <>
                            <Lock className="mr-1 size-4" />
                            <span>Private</span>
                        </>
                    )}
                </Button>
            </DialogTrigger>
            {/* AIDEV-NOTE: Only render DialogContent when dialog is open.
                Radix Dialog renders content even when closed, which causes
                Radix Select to fire spurious onValueChange events (fixes EVO-761) */}
            {open && (
                <DialogContent className="w-96">
                    <DialogHeader>
                        <DialogTitle>Sharing Preference</DialogTitle>
                    </DialogHeader>
                    <ResearchReleasePanel sessionId={sessionId} />
                </DialogContent>
            )}
        </Dialog>
    );
}
